import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  CanAccess,
  useDataProvider,
  useGetMany,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Link, Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import type { CrmDataProvider } from "../providers/types";
import { LeadTypeField } from "../deals/LeadTypeField";
import type { Deal, LeadType, Project } from "../types";
import {
  EMPTY_LOG_FILTERS,
  LOG_PAGE_SIZE,
  buildLogFilter,
  describeAssignment,
  type AssignmentLogFilters,
  type LeadAssignmentLogRow,
} from "./assignmentLogRules";

/** Query key prefix, invalidated by the "Asignar automaticamente" action. */
export const ASSIGNMENT_LOG_QUERY_KEY = "lead_assignments_log";

const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9";

/**
 * Almikia Epic EL (TASK-010): "Bitacora de asignacion". A plain, append-only list of who
 * received which lead, when, through which channel and by which method, plus the outcomes where
 * nobody was available. Which rows come back is decided by RLS and the security_invoker
 * `lead_assignments_summary` view (direccion all, lead_manager her scope, advisors only the leads
 * they received); the screen only filters and paginates. Always newest first: no counts per
 * advisor, no ranking, no comparison anywhere.
 */
export const AssignmentLog = () => (
  <CanAccess
    resource="lead_assignment_log"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <AssignmentLogContent />
  </CanAccess>
);

AssignmentLog.path = "/lead_assignment_log";

const formatWhen = (iso: string) =>
  new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Mexico_City",
  }).format(new Date(iso));

const AssignmentLogContent = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [filters, setFilters] =
    useState<AssignmentLogFilters>(EMPTY_LOG_FILTERS);
  const [page, setPage] = useState(1);

  const projects = useQuery({
    queryKey: [ASSIGNMENT_LOG_QUERY_KEY, "projects"],
    queryFn: async () =>
      (
        await dataProvider.getList<Project>("projects", {
          pagination: { page: 1, perPage: 200 },
          sort: { field: "name", order: "ASC" },
          filter: {},
        })
      ).data,
  });
  const channels = useQuery({
    queryKey: [ASSIGNMENT_LOG_QUERY_KEY, "channels"],
    queryFn: () => dataProvider.getLeadAssignmentChannels(),
  });
  const log = useQuery({
    queryKey: [ASSIGNMENT_LOG_QUERY_KEY, "rows", filters, page],
    placeholderData: keepPreviousData,
    queryFn: () =>
      dataProvider.getList<LeadAssignmentLogRow>("lead_assignments_summary", {
        pagination: { page, perPage: LOG_PAGE_SIZE },
        sort: { field: "assigned_at", order: "DESC" },
        filter: buildLogFilter(filters),
      }),
  });

  const setFilter = (patch: Partial<AssignmentLogFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const rows = log.data?.data ?? [];
  const total = log.data?.total ?? 0;
  // EL3: the view carries no lead type, so one batched read per page resolves it (never one
  // request per row).
  const dealIds = [...new Set(rows.map((row) => row.deal_id))];
  const deals = useGetMany<Deal>(
    "deals",
    { ids: dealIds },
    { enabled: dealIds.length > 0 },
  );
  const leadTypeOf = (dealId: Identifier) =>
    deals.data?.find((deal) => String(deal?.id) === String(dealId))?.lead_type;
  const hasNext = page * LOG_PAGE_SIZE < total;

  return (
    <div className="mt-2 max-w-3xl mx-auto space-y-4 pb-8">
      <h1 className="px-1 text-xl font-semibold">
        {translate("crm.assignmentLog.title")}
      </h1>
      <p className="px-1 text-sm text-muted-foreground">
        {translate("crm.assignmentLog.intro")}
      </p>

      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="log-project">
              {translate("crm.assignmentLog.filter_project")}
            </Label>
            <select
              id="log-project"
              className={SELECT_CLASS}
              value={filters.projectId}
              onChange={(e) => setFilter({ projectId: e.target.value })}
            >
              <option value="">{translate("crm.assignmentLog.all")}</option>
              {(projects.data ?? []).map((project) => (
                <option key={project.id} value={String(project.id)}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="log-channel">
              {translate("crm.assignmentLog.filter_channel")}
            </Label>
            <select
              id="log-channel"
              className={SELECT_CLASS}
              value={filters.channelCode}
              onChange={(e) => setFilter({ channelCode: e.target.value })}
            >
              <option value="">{translate("crm.assignmentLog.all")}</option>
              {(channels.data ?? []).map((channel) => (
                <option key={channel.code} value={channel.code}>
                  {channel.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="log-from">
              {translate("crm.assignmentLog.filter_from")}
            </Label>
            <Input
              id="log-from"
              type="date"
              className="h-11 sm:h-9"
              value={filters.dateFrom}
              onChange={(e) => setFilter({ dateFrom: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="log-to">
              {translate("crm.assignmentLog.filter_to")}
            </Label>
            <Input
              id="log-to"
              type="date"
              className="h-11 sm:h-9"
              value={filters.dateTo}
              onChange={(e) => setFilter({ dateTo: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      {log.error ? (
        <ListErrorState
          message={translate("crm.common.list_error")}
          onRetry={() => log.refetch()}
        />
      ) : !log.data ? (
        <ListCardsSkeleton rows={3} />
      ) : rows.length === 0 ? (
        <ListEmptyState
          className="min-h-0 py-4"
          title={translate("crm.assignmentLog.empty")}
          description={translate("crm.assignmentLog.empty_hint")}
        />
      ) : (
        <ul className="space-y-2" data-testid="assignment-log-list">
          {rows.map((row) => {
            const labels = describeAssignment(row);
            return (
              <li key={row.id}>
                <Card>
                  <CardContent className="space-y-1 p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <Link
                        to={`/deals/${row.deal_id}/show`}
                        className="font-medium underline-offset-2 hover:underline"
                      >
                        {row.deal_name ?? translate("crm.assignmentLog.lead")}
                      </Link>
                      <span className="text-sm text-muted-foreground">
                        {formatWhen(row.assigned_at)}
                      </span>
                    </div>
                    <p className="text-sm">
                      {labels.isUnassigned
                        ? translate("crm.assignmentLog.unassigned")
                        : translate("crm.assignmentLog.received_by", {
                            name:
                              labels.recipient ??
                              translate("crm.assignmentLog.lead"),
                          })}
                    </p>
                    {row.advisor_code || row.previous_advisor_code ? (
                      <p
                        className="text-sm text-muted-foreground"
                        data-testid="assignment-log-claves"
                      >
                        {[
                          row.advisor_code
                            ? translate("crm.assignmentLog.clave", {
                                code: row.advisor_code,
                              })
                            : null,
                          row.previous_advisor_code
                            ? translate("crm.assignmentLog.previous_clave", {
                                code: row.previous_advisor_code,
                              })
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    ) : null}
                    <p className="text-sm text-muted-foreground">
                      {[
                        row.project_name,
                        translate("crm.assignmentLog.channel_value", {
                          channel: labels.channel,
                        }),
                        translate(
                          `crm.assignmentLog.method.${labels.methodKey}`,
                        ),
                        labels.reasonKey
                          ? translate("resources.guardias.reason_label", {
                              reason: translate(
                                `resources.guardias.reason.${labels.reasonKey}`,
                              ),
                            })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    {leadTypeOf(row.deal_id) ? (
                      <LeadTypeField
                        leadType={leadTypeOf(row.deal_id) as LeadType}
                        className="pt-1"
                      />
                    ) : null}
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {total > LOG_PAGE_SIZE ? (
        <div className="flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            disabled={page === 1}
            onClick={() => setPage((current) => current - 1)}
          >
            {translate("crm.assignmentLog.previous")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            disabled={!hasNext}
            onClick={() => setPage((current) => current + 1)}
          >
            {translate("crm.assignmentLog.next")}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
